import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/pro-light-svg-icons/faPlay';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { Panel, PanelBody } from '../../components/primitives/Panel';
import { useReplyThread } from '../hooks/useReplyThread';
import { secondsToClock, shortDate } from '../outreachFormat';
import { ThreadHeader } from './ThreadHeader';
import { PaymentWarning } from './PaymentWarning';
import { PlacementBox } from './PlacementBox';
import { ReplyAnswer } from './ReplyAnswer';
import { ReplyAsks } from './ReplyAsks';
import { ThreadSide } from './ThreadSide';
import { inboxMessages as messages } from './inboxMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Inbox.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * One thread (design outreach-v2 m1, m2, m4): the listen line of the pitch page, our pitch, the
 * reply, what they ask for, a placement, the payment warning (fixed copy, no suggested answer) or
 * the answer; the side panel next to it (under it on a narrow screen).
 */
const ReplyThreadComponent: React.FC<{ accountId: number; replyUuid: string | null }> = ({ accountId, replyUuid }) => {
  const intl = useIntl();
  const thread = useReplyThread(accountId, replyUuid);

  if (replyUuid === null) {
    return (
      <Panel as="section" pad>
        <p className={shared.small}>{intl.formatMessage(messages.pickThread)}</p>
      </Panel>
    );
  }
  if (thread.isLoading) {
    return <LoadingView />;
  }
  if (thread.error !== null || thread.thread === undefined) {
    return <ErrorView error={thread.error ?? undefined} retry={thread.retry} />;
  }
  const reply = thread.thread;

  return (
    <div className={styles.threadWrap}>
      <Panel as="section">
        <ThreadHeader accountId={accountId} thread={reply} />
        <PanelBody>
          <div className={styles.thread} data-testid="reply-thread">
            {reply.listen !== null && (
              <div className={styles.listen}>
                <FontAwesomeIcon icon={faPlay} />
                <span className={shared.grow}>
                  {intl.formatMessage(messages.listened, {
                    time: secondsToClock(reply.listen.seconds),
                    plays: reply.listen.plays,
                  })}
                </span>
                <span className={shared.mono}>{shortDate(intl, reply.listen.lastAt)}</span>
              </div>
            )}
            <div className={`${styles.bubble} ${styles.collapsed}`}>
              <div className={styles.bubbleHead}>
                <b>{intl.formatMessage(messages.you)}</b>
                <span>{shortDate(intl, reply.message.sentAt)}</span>
              </div>
              <p className={styles.bubbleText}>{reply.message.body}</p>
            </div>
            {reply.text !== '' && (
              <div className={`${styles.bubble} ${styles.bubbleThem}`}>
                <div className={styles.bubbleHead}>
                  <b>{reply.contactName}</b>
                  <span>{shortDate(intl, reply.receivedAt)}</span>
                </div>
                <p className={styles.bubbleText}>{reply.text}</p>
              </div>
            )}
            <ReplyAsks asks={reply.asks} />
            {reply.placement !== null && <PlacementBox song={reply.songTitle} placement={reply.placement} />}
            {reply.label === 'asksForPayment' ? (
              <PaymentWarning accountId={accountId} replyUuid={reply.replyUuid} />
            ) : (
              <ReplyAnswer accountId={accountId} thread={reply} />
            )}
          </div>
        </PanelBody>
      </Panel>
      <ThreadSide accountId={accountId} thread={reply} />
    </div>
  );
};

export const ReplyThread = TypedMemo(ReplyThreadComponent);
